(() => {
  if (document.body.classList.contains("is-505")) return;

  const LAB_KEY = "fh-lab-v6";
  const DEFAULT = {
    pre: 1.49,
    load: 1.46,
    amb: 1.39,
    pick: 1.67,
    drop: 1.64,
    open: 1.24,
    open1: 1.24,
    open2: 1.24,
    close: 1.2,
    cmd: 1.55,
    calc: 1.43,
    type: 0.21,
    pull: 1.55,
    pull1: 1.55,
    pull2: 1.55,
    pull3: 1.55,
    pull4: 1.55,
    dropwing: 1.55,
    dropwings1: 1.55,
    dropwings2: 1.55,
    logofall: 1.55
  };
  const GROUPS = {
    pull: ["pull1", "pull2", "pull3", "pull4"],
    dropwing: ["dropwings1", "dropwings2"]
  };
  const HOLD = new Set(["pre", "load"]);
  const WING = /^(pull|dropwing|logofall)/;
  const phone = window.matchMedia("(max-width: 900px)").matches;

  const urls = (window.FH && window.FH.sfx) || {};
  const els = {};
  const buffers = {};
  const decoding = {};
  const liveSrc = {};
  let vols = { ...DEFAULT };
  let ambOn = false;
  let house = false;
  let ctx = null;
  let unlocked = false;
  let ambSrc = null;
  let ambGain = null;
  let ambNext = 0;
  let ambWatch = 0;

  const clamp = (n) => Math.max(0, Math.min(2, Number(n)));
  const AC = window.AudioContext || window.webkitAudioContext;
  const gainOf = (name) => {
    const v = vols[name] ?? 1;
    return WING.test(name) ? v * 0.5 : v;
  };

  const getCtx = () => {
    if (ctx) return ctx;
    if (!AC) return null;
    try { ctx = new AC(); } catch (_) { return null; }
    window.FH = window.FH || {};
    window.FH.audioCtx = ctx;
    return ctx;
  };

  const kickCtx = () => {
    const c = getCtx();
    if (c && c.state === "suspended") c.resume().catch(() => {});
    return c;
  };

  const decode = (c, raw) => new Promise((resolve, reject) => {
    const copy = raw.slice(0);
    let settled = false;
    const ok = (buf) => { if (!settled) { settled = true; resolve(buf); } };
    const fail = (err) => { if (!settled) { settled = true; reject(err); } };
    try {
      const ret = c.decodeAudioData(copy, ok, fail);
      if (ret && typeof ret.then === "function") ret.then(ok, fail);
    } catch (err) {
      fail(err);
    }
  });

  const pullVols = (sfx) => {
    if (!sfx || typeof sfx !== "object") return;
    Object.keys(DEFAULT).forEach((k) => {
      if (sfx[k] != null && isFinite(Number(sfx[k]))) vols[k] = clamp(sfx[k]);
    });
    vols.open1 = vols.open;
    vols.open2 = vols.open;
    GROUPS.pull.forEach((k) => { vols[k] = vols.pull; });
    GROUPS.dropwing.forEach((k) => { vols[k] = vols.dropwing; });
  };

  try {
    pullVols(JSON.parse(localStorage.getItem(LAB_KEY) || "{}").sfx);
  } catch (_) { /* */ }

  Object.keys(DEFAULT).forEach((k) => {
    if (!urls[k]) return;
    const a = new Audio();
    a.preload = k === "amb" ? "auto" : "metadata";
    a.playsInline = true;
    a.src = urls[k];
    if (k === "amb") a.loop = true;
    els[k] = a;
  });

  const loadBuf = (k) => {
    if (!urls[k] || buffers[k] || decoding[k]) return decoding[k];
    const c = getCtx();
    if (!c) return null;
    decoding[k] = fetch(urls[k])
      .then((r) => r.arrayBuffer())
      .then((raw) => decode(c, raw))
      .then((buf) => {
        buffers[k] = buf;
        delete decoding[k];
        if (k === "amb" && ambOn) startAmb();
        return buf;
      })
      .catch(() => { delete decoding[k]; });
    return decoding[k];
  };

  getCtx();
  Object.keys(urls).forEach((k) => loadBuf(k));

  const stopSrc = (name) => {
    const src = liveSrc[name];
    if (!src) return;
    try { src.stop(); } catch (_) { /* */ }
    try { src.disconnect(); } catch (_) { /* */ }
    delete liveSrc[name];
  };

  const stop = (name) => {
    stopSrc(name);
    const a = els[name];
    if (a && name !== "amb") {
      a.pause();
      try { a.currentTime = 0; } catch (_) { /* */ }
    }
  };

  const htmlVol = (a, v) => {
    if (!a) return;
    a.volume = Math.max(0, Math.min(1, Number(v) || 0));
  };

  const ambLevel = () => {
    if (!ambOn || vols.amb <= 0 || document.hidden) return 0;
    return vols.amb;
  };

  const stopHtmlAmb = () => {
    const a = els.amb;
    if (!a) return;
    a.pause();
    try { a.currentTime = 0; } catch (_) { /* */ }
  };

  const stopBufAmb = () => {
    if (ambWatch) {
      clearTimeout(ambWatch);
      ambWatch = 0;
    }
    if (ambSrc) {
      try { ambSrc.stop(); } catch (_) { /* */ }
      try { ambSrc.disconnect(); } catch (_) { /* */ }
      ambSrc = null;
    }
    ambNext = 0;
  };

  const duckAmb = () => {
    const c = ctx;
    if (!ambGain || !c) return;
    const now = c.currentTime;
    ambGain.gain.cancelScheduledValues(now);
    ambGain.gain.setTargetAtTime(Math.max(0.0001, ambLevel()), now, 0.04);
  };

  const scheduleAmb = () => {
    const c = kickCtx();
    const buf = buffers.amb;
    if (!c || !buf || !ambOn || vols.amb <= 0) return;
    if (c.state === "suspended") return;
    if (!ambGain) {
      ambGain = c.createGain();
      ambGain.gain.value = Math.max(0.0001, ambLevel());
      ambGain.connect(c.destination);
    } else duckAmb();
    stopHtmlAmb();
    const fade = Math.min(0.12, Math.max(0.04, buf.duration * 0.04));
    const dur = buf.duration;
    const now = c.currentTime;
    const at = Math.max(now, ambNext || now);
    const src = c.createBufferSource();
    const g = c.createGain();
    src.buffer = buf;
    src.connect(g);
    g.connect(ambGain);
    g.gain.setValueAtTime(0.0001, at);
    g.gain.linearRampToValueAtTime(1, at + fade);
    const hold = Math.max(at + fade, at + dur - fade);
    g.gain.setValueAtTime(1, hold);
    g.gain.linearRampToValueAtTime(0.0001, at + dur);
    try {
      src.start(at);
      src.stop(at + dur + 0.02);
    } catch (_) {
      return;
    }
    ambSrc = src;
    ambNext = at + dur - fade;
    const wait = Math.max(40, (ambNext - c.currentTime) * 1000 - 80);
    if (ambWatch) clearTimeout(ambWatch);
    ambWatch = setTimeout(() => {
      ambWatch = 0;
      if (ambOn) scheduleAmb();
    }, wait);
  };

  const startAmb = () => {
    ambOn = true;
    const c = kickCtx();
    if (buffers.amb && c && c.state !== "suspended") {
      if (!ambSrc && !ambNext) scheduleAmb();
      else duckAmb();
      return;
    }
    loadBuf("amb");
    const a = els.amb;
    if (!a) return;
    htmlVol(a, Math.min(1, vols.amb));
    if (ambLevel() <= 0) {
      a.pause();
      return;
    }
    const p = a.play();
    if (p && p.catch) p.catch(() => {});
  };

  const playHtml = (name, done) => {
    const a = els[name];
    if (!a) return done();
    htmlVol(a, Math.min(1, gainOf(name)));
    try { a.currentTime = 0; } catch (_) { /* */ }
    const finish = () => done();
    a.addEventListener("ended", finish, { once: true });
    a.addEventListener("error", finish, { once: true });
    const p = a.play();
    if (p && p.catch) {
      p.catch(() => finish());
    }
  };

  const playBuffer = (name, done) => {
    const c = kickCtx();
    const buf = buffers[name];
    if (!c || !buf || c.state === "suspended") return false;
    if (HOLD.has(name)) stopSrc(name);
    const node = c.createBufferSource();
    const g = c.createGain();
    g.gain.value = gainOf(name);
    node.buffer = buf;
    node.connect(g);
    g.connect(c.destination);
    node.onended = () => {
      if (liveSrc[name] === node) delete liveSrc[name];
      done();
    };
    if (HOLD.has(name)) liveSrc[name] = node;
    try {
      node.start(0);
    } catch (_) {
      return false;
    }
    return true;
  };

  const play = (name) => new Promise((resolve) => {
    const done = () => resolve();
    if (document.body.classList.contains("is-505")) return done();
    const group = GROUPS[name];
    if (group && group.length) {
      if (vols[name] <= 0) return done();
      name = group[Math.floor(Math.random() * group.length)];
    }
    if (vols[name] <= 0) return done();
    if (name === "type") {
      const c = kickCtx();
      if (!c) return done();
      const sr = c.sampleRate;
      const dur = 0.016 + Math.random() * 0.022;
      const n = Math.max(48, Math.floor(sr * dur));
      const buf = c.createBuffer(1, n, sr);
      const d = buf.getChannelData(0);
      const freq = 980 + Math.random() * 1120;
      const click = 0.07 + Math.random() * 0.2;
      const decay = 64 + Math.random() * 58;
      const noiseAmt = 0.5 + Math.random() * 0.4;
      for (let i = 0; i < n; i++) {
        const t = i / sr;
        const env = Math.exp(-t * decay) * (1 - i / n);
        const noise = (Math.random() * 2 - 1) * noiseAmt;
        const tone = Math.sign(Math.sin(t * 2 * Math.PI * freq + Math.random() * 0.4)) * click;
        d[i] = (noise * 0.68 + tone) * env;
      }
      const node = c.createBufferSource();
      const g = c.createGain();
      g.gain.value = vols.type * (0.58 + Math.random() * 0.44);
      node.buffer = buf;
      node.playbackRate.value = 0.86 + Math.random() * 0.32;
      node.connect(g);
      g.connect(c.destination);
      node.onended = done;
      try { node.start(0); } catch (_) { return done(); }
      return;
    }
    if (!urls[name]) return done();
    if (name === "amb") {
      startAmb();
      return done();
    }
    if (name === "pre" && (house || phone)) return done();
    if (HOLD.has(name)) stop(name);
    if (playBuffer(name, done)) return;
    const pending = decoding[name] || loadBuf(name);
    if (pending && typeof pending.then === "function") {
      let settled = false;
      const once = () => {
        if (settled) return;
        settled = true;
        if (playBuffer(name, done)) return;
        playHtml(name, done);
      };
      const t = setTimeout(once, phone ? 180 : 80);
      pending.then(() => {
        clearTimeout(t);
        once();
      });
      return;
    }
    playHtml(name, done);
  });

  const unlock = () => {
    const c = kickCtx();
    if (unlocked) {
      if (ambOn && buffers.amb && !ambSrc) scheduleAmb();
      return;
    }
    unlocked = true;
    if (c) {
      try {
        const buf = c.createBuffer(1, 1, 22050);
        const src = c.createBufferSource();
        src.buffer = buf;
        src.connect(c.destination);
        src.start(0);
      } catch (_) { /* */ }
    }
    if (ambOn) startAmb();
  };

  ["pointerdown", "keydown", "touchstart"].forEach((ev) => {
    document.addEventListener(ev, unlock, { capture: true });
  });
  document.addEventListener("visibilitychange", () => {
    const c = getCtx();
    if (!document.hidden && c && c.state === "suspended") c.resume().catch(() => {});
    if (buffers.amb && ambOn) duckAmb();
    else if (ambOn) startAmb();
    else stopHtmlAmb();
  });
  addEventListener("fh:lab", (e) => {
    pullVols(e.detail && e.detail.sfx);
    if (ambOn) {
      if (buffers.amb) duckAmb();
      else startAmb();
    }
  });

  window.FH = window.FH || {};
  window.FH.playSfx = play;
  window.FH.stopSfx = (name) => {
    if (name === "amb") {
      ambOn = false;
      stopBufAmb();
      stopHtmlAmb();
      duckAmb();
      return;
    }
    stop(name);
  };
  window.FH.setHtmlVol = htmlVol;
  window.FH.startAmb = startAmb;
  window.FH.enterHouse = () => {
    house = true;
    stop("pre");
  };
})();
